import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { Logo, yearLabel } from "@/components/shared";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Home, BookOpen, Gamepad2, FolderOpen, StickyNote, LogOut, Shield, Milestone, Bell, CheckCircle2, AlertTriangle, Briefcase, FileText, Link2, Volume2, Send, Flame, Sun, Camera, Award } from "lucide-react";

let sharedAudioCtx = null;
function playChime() {
  try {
    if (!sharedAudioCtx) sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = sharedAudioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    [660, 880].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.18, now + i * 0.15 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.15 + 0.3);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.15);
      osc.stop(now + i * 0.15 + 0.32);
    });
  } catch (err) {
    if (process.env.NODE_ENV === "development") console.warn("Chime audio unavailable:", err);
  }
}

function notifRoute(n) {
  if (n.kind === "pfmp") return "/progression";
  if (n.kind === "career") return "/recherche-stage";
  if (n.kind === "pfmp_portfolio") return "/pfmp-portfolio";
  if (n.kind === "pfmp_portfolio_reminder") return "/pfmp-portfolio";
  if (n.kind === "cap_dossier") return "/portfolio";
  if (n.kind === "inactivity") return "/cours";
  if (n.kind === "rentree") return "/cours";
  return "/cours";
}

function NotifIcon({ n }) {
  if (n.kind === "proof" || n.kind === "career" || n.kind === "pfmp_portfolio") {
    return n.status === "validated"
      ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
      : <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />;
  }
  if (n.kind === "pfmp") return <Briefcase className="h-4 w-4 text-emerald-600 shrink-0" />;
  if (n.kind === "pfmp_portfolio_reminder") return <Briefcase className="h-4 w-4 text-sky-600 shrink-0" />;
  if (n.kind === "cap_dossier") return <Award className="h-4 w-4 text-indigo-600 shrink-0" />;
  if (n.kind === "inactivity") return <Flame className="h-4 w-4 text-orange-500 shrink-0" />;
  if (n.kind === "rentree") return <Sun className="h-4 w-4 text-amber-500 shrink-0" />;
  if (n.status === "link") return <Link2 className="h-4 w-4 text-orange-500 shrink-0" />;
  if (n.status === "doc") return <FileText className="h-4 w-4 text-orange-500 shrink-0" />;
  return <BookOpen className="h-4 w-4 text-orange-500 shrink-0" />;
}

function notifTitleColor(n) {
  if (n.kind === "proof" || n.kind === "career" || n.kind === "pfmp_portfolio") return n.status === "validated" ? "text-emerald-700" : "text-amber-700";
  if (n.kind === "pfmp") return "text-emerald-700";
  if (n.kind === "pfmp_portfolio_reminder") return "text-sky-700";
  if (n.kind === "cap_dossier") return "text-indigo-700";
  if (n.kind === "inactivity") return "text-orange-700";
  if (n.kind === "rentree") return "text-amber-700";
  return "text-slate-800";
}

const NAV = [
  { to: "/", label: "Accueil", icon: Home, end: true, testid: "nav-accueil" },
  { to: "/cours", label: "Mes cours", icon: BookOpen, testid: "nav-cours" },
  { to: "/progression", label: "Progression", icon: Milestone, testid: "nav-progression" },
  { to: "/entrainement", label: "M'entraîner", icon: Gamepad2, testid: "nav-entrainement" },
  { to: "/portfolio", label: "Mon portfolio", icon: FolderOpen, testid: "nav-portfolio" },
  { to: "/recherche-stage", label: "Recherche de stage", icon: Send, testid: "nav-recherche-stage" },
  { to: "/pfmp-portfolio", label: "Portfolio PFMP", icon: Camera, testid: "nav-pfmp-portfolio" },
  { to: "/memos", label: "Mémos", icon: StickyNote, testid: "nav-memos" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [toRevise, setToRevise] = useState(0);
  const [newProofs, setNewProofs] = useState(0);
  const [feed, setFeed] = useState({ unseen: 0, items: [] });
  const [bellOpen, setBellOpen] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const prevAudioKeysRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") {
      const fetchStu = () => {
        api.get("/notifications").then(({ data }) => setToRevise(data.to_revise || 0)).catch(() => {});
        api.get("/notifications/feed").then(({ data }) => {
          const prevKeys = prevAudioKeysRef.current;
          if (prevKeys) {
            const hasNewAudio = data.items.some((it) => it.kind === "proof" && it.has_audio && it.unseen && !prevKeys.has(it.key));
            if (hasNewAudio) playChime();
          }
          prevAudioKeysRef.current = new Set(data.items.filter((it) => it.has_audio).map((it) => it.key));
          setFeed(data);
        }).catch(() => {});
      };
      fetchStu();
      const t = setInterval(fetchStu, 30000);
      return () => clearInterval(t);
    }
    const fetchNew = () =>
      api.get("/admin/notifications").then(({ data }) => setNewProofs(data.new_proofs || 0)).catch(() => {});
    fetchNew();
    const t = setInterval(fetchNew, 30000);
    return () => clearInterval(t);
  }, [user]);

  const openBell = async (open) => {
    setBellOpen(open);
    if (open && feed.unseen > 0) {
      try { await api.post("/notifications/seen"); } catch (err) { if (process.env.NODE_ENV === "development") console.warn("Mark notifications seen failed:", err); }
      setFeed((f) => ({ ...f, unseen: 0, items: f.items.map((i) => ({ ...i, unseen: false })) }));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const Badge = ({ count, testid = "nav-badge" }) =>
    count > 0 ? (
      <span data-testid={testid} className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold">
        {count}
      </span>
    ) : null;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <NavLink to="/" data-testid="nav-home-logo"><Logo /></NavLink>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-bold transition-colors ${
                    isActive ? "bg-orange-100 text-orange-700" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                  }`
                }
              >
                <n.icon className="h-4 w-4" />
                {n.label}
                {n.to === "/cours" && <Badge count={toRevise} />}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `hidden sm:flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-bold transition-colors ${
                    isActive ? "bg-sky-100 text-sky-700" : "text-sky-600 hover:bg-sky-50"
                  }`
                }
              >
                <Shield className="h-4 w-4" /> Espace prof
                <Badge count={newProofs} testid="nav-admin-badge" />
              </NavLink>
            )}
            {user && user.role !== "admin" && (
              <Popover open={bellOpen} onOpenChange={openBell}>
                <PopoverTrigger asChild>
                  <button
                    data-testid="notif-bell"
                    className="relative h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors"
                    title="Notifications"
                  >
                    <Bell className="h-4 w-4" />
                    {feed.unseen > 0 && (
                      <span data-testid="notif-bell-badge" className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-extrabold">
                        {feed.unseen}
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-0 rounded-2xl overflow-hidden" data-testid="notif-panel">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="font-display font-semibold text-slate-900">Notifications</p>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {feed.items.length === 0 ? (
                      <p className="text-sm text-slate-400 font-medium px-4 py-8 text-center">Aucune notification pour l'instant.</p>
                    ) : (
                      feed.items.map((n) => (
                        <div
                          key={n.key}
                          role="button"
                          tabIndex={0}
                          data-testid={`notif-item-${n.key}`}
                          onClick={() => { setBellOpen(false); navigate(notifRoute(n)); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { setBellOpen(false); navigate(notifRoute(n)); } }}
                          className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${n.unseen ? "bg-orange-50/50" : ""}`}
                        >
                          <div className="flex items-center gap-2">
                            <NotifIcon n={n} />
                            <span className={`text-sm font-bold ${notifTitleColor(n)}`}>{n.title}</span>
                            {n.has_audio && <Volume2 className="h-3.5 w-3.5 text-orange-500 shrink-0" data-testid={`notif-audio-icon-${n.key}`} />}
                          </div>
                          {n.subtitle && <p className="text-xs text-slate-500 font-medium mt-0.5 truncate">{n.subtitle}</p>}
                          {n.comment && <p className="text-xs text-slate-400 italic mt-0.5 truncate">« {n.comment} »</p>}
                          {n.has_audio && (
                            <>
                              <span
                                role="button"
                                tabIndex={0}
                                data-testid={`notif-audio-btn-${n.key}`}
                                onClick={(e) => { e.stopPropagation(); setPlayingAudio((p) => (p === n.key ? null : n.key)); }}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); setPlayingAudio((p) => (p === n.key ? null : n.key)); } }}
                                className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-full px-2.5 py-1"
                              >
                                <Volume2 className="h-3 w-3" /> {playingAudio === n.key ? "Masquer l'audio" : "Écouter le commentaire audio"}
                              </span>
                              {playingAudio === n.key && (
                                <audio
                                  src={fileUrl(n.audio_id)}
                                  controls
                                  autoPlay
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`notif-audio-player-${n.key}`}
                                  className="w-full h-8 mt-1.5"
                                />
                              )}
                            </>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            <div className="hidden lg:flex flex-col items-end leading-tight mr-1">
              <span className="text-sm font-bold text-slate-800">{user?.name}</span>
              <span className="text-[11px] text-slate-400 font-semibold">
                {user?.role === "admin" ? "Professeur" : yearLabel(user?.year)}
              </span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="h-9 w-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors"
              title="Se déconnecter"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* mobile nav */}
        <nav className="md:hidden flex items-center gap-1 overflow-x-auto px-3 pb-2.5 -mt-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${
                  isActive ? "bg-orange-100 text-orange-700" : "text-slate-500 bg-slate-50"
                }`
              }
            >
              <n.icon className="h-3.5 w-3.5" /> {n.label}
              {n.to === "/cours" && <Badge count={toRevise} testid="nav-cours-badge-mobile" />}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 bg-white mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-sm font-bold text-slate-700 font-display">MerryZone — le magasin du lycée · Espace élève</p>
          <p className="text-xs text-slate-400 font-semibold">CAP Équipier Polyvalent du Commerce · Académie de Lille</p>
        </div>
      </footer>
    </div>
  );
}
